<script setup>
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  steps: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'delete', 'add']);

const { t } = useI18n();

const messageTypeIcons = {
  text: 'i-lucide-message-square',
  image: 'i-lucide-image',
  video: 'i-lucide-video',
  audio: 'i-lucide-music',
  document: 'i-lucide-file-text',
  template: 'i-lucide-layout-template',
};

const formatDelay = step => {
  const parts = [];
  if (step.delay_days > 0) parts.push(`${step.delay_days}d`);
  if (step.delay_hours > 0) parts.push(`${step.delay_hours}h`);
  if (step.delay_minutes > 0) parts.push(`${step.delay_minutes}m`);
  return parts.length
    ? parts.join(' ')
    : t('CAMPAIGN.FOLLOW_UP.STEP.IMMEDIATE');
};

const truncateContent = (content, maxLength = 100) => {
  if (!content || content.length <= maxLength) return content;
  return content.substring(0, maxLength) + '...';
};
</script>

<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <h3 class="text-lg font-medium text-n-slate-12">
        {{ t('CAMPAIGN.FOLLOW_UP.STEP.TITLE') }}
      </h3>
      <Button size="small" icon="i-lucide-plus" @click="emit('add')">
        {{ t('CAMPAIGN.FOLLOW_UP.STEP.ADD') }}
      </Button>
    </div>

    <div class="relative space-y-4">
      <!-- Timeline line -->
      <div
        v-if="steps.length > 1"
        class="absolute bottom-8 left-6 top-8 w-0.5 bg-n-alpha-3"
      />

      <div v-for="step in steps" :key="step.id" class="relative flex gap-4">
        <!-- Step number indicator -->
        <div
          class="z-10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border-2"
          :class="[
            step.active
              ? 'border-n-brand bg-n-brand/5 text-n-brand'
              : 'border-n-weak bg-n-alpha-2 text-n-slate-10',
          ]"
        >
          {{ step.step_order }}
        </div>

        <!-- Step content -->
        <div class="flex-1 rounded-lg border border-n-weak bg-n-solid-1 p-4">
          <div class="flex items-start justify-between">
            <div>
              <div class="flex items-center gap-2">
                <span
                  :class="messageTypeIcons[step.message_type]"
                  class="h-4 w-4 text-n-slate-10"
                />
                <h4 class="font-medium text-n-slate-12">
                  {{
                    step.name ||
                    `${t('CAMPAIGN.FOLLOW_UP.STEPS')} ${step.step_order}`
                  }}
                </h4>
                <span
                  v-if="!step.active"
                  class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-10"
                >
                  {{ t('CAMPAIGN.FOLLOW_UP.STEP.INACTIVE') }}
                </span>
              </div>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ truncateContent(step.content) }}
              </p>
            </div>
            <div class="flex gap-1">
              <Button
                size="tiny"
                variant="faded"
                color="slate"
                icon="i-lucide-pencil"
                @click="emit('edit', step)"
              />
              <Button
                size="tiny"
                variant="faded"
                color="ruby"
                icon="i-lucide-trash-2"
                @click="emit('delete', step)"
              />
            </div>
          </div>

          <div class="mt-3 flex items-center gap-4 text-xs text-n-slate-10">
            <span class="flex items-center gap-1">
              <span class="i-lucide-clock h-3 w-3" />
              {{ t('CAMPAIGN.FOLLOW_UP.STEP.DELAY_PREFIX') }}
              {{ formatDelay(step) }}
            </span>
            <span class="flex items-center gap-1">
              <span class="i-lucide-send h-3 w-3" />
              {{ step.sent_count || 0 }}
              {{ t('CAMPAIGN.FOLLOW_UP.STEP.SENT_COUNT') }}
            </span>
            <span v-if="step.executions_count" class="flex items-center gap-1">
              <span class="i-lucide-users h-3 w-3" />
              {{ step.executions_count }}
              {{ t('CAMPAIGN.FOLLOW_UP.STEP.IN_QUEUE') }}
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="steps.length === 0 && !isLoading"
        class="py-8 text-center text-sm text-n-slate-10"
      >
        {{ t('CAMPAIGN.FOLLOW_UP.STEP.EMPTY') }}
      </div>
    </div>
  </div>
</template>
